import { useQuery } from '@tanstack/react-query'
import { coreApplicationsDashboardQuery } from '../api/proactivitee'
import { BoardCard, BoardGrid, BoardLook, BoardScale, BoardStat, BoardStrip } from '../dashboard/BoardLook'
import { toneOfColor } from '../dashboard/boardTone'
import { Content } from '../ui/Shell'
import { APP_ICON_FACE, DEFAULT_APP_ICON } from './appIcon'

/**
 * Core Applications Dashboard, pe desenul tablourilor de bord.
 * =============================================================================
 *
 * ACELEASI CIFRE, ALTA HAINA. Ecranul arata mai departe fix ce arata si
 * inainte: cele cinci contoare de infrastructura, cele doua procente de stare,
 * si cate o dala pentru FIECARE aplicatie a clientului, cu numarul de masini,
 * cate sunt sanatoase si cate nu. Nimic nu se citeste altfel si nimic nu se
 * citeste din alta parte - raspunsul e acelasi
 * `GET /api/v2/proactivitee/core-applications/dashboard`.
 *
 * Ce se schimba e desenul: dalele colorate de la Metronic (`dashboard-stat`,
 * `.core_app_box`) lasa locul cardului tablourilor - alb, colturi de 14px,
 * umbra in loc de chenar, cifra de 40px, placuta colorata a modulului si
 * culorile de stare din `tokens.css`. Acelasi limbaj ca
 * `/app/proactivitee/dashboard` si ca celelalte patru tablouri, ca sa nu mai
 * arate a ecran ramas in urma.
 *
 * -----------------------------------------------------------------------------
 * DE CE NU INTRA IN CATALOG (si de ce asta NU e o conversie)
 * -----------------------------------------------------------------------------
 *
 * Tablourile noi isi iau cardurile dintr-un CATALOG scris de mana
 * (`application/Services/Dashboard/Catalog.php`): fiecare card e o cifra cu o
 * tinta, doua praguri, un drept si o linie de istorie pe zile. Ecranul asta nu
 * incape acolo, si nici n-ar trebui:
 *
 *   - lista de aplicatii e A CLIENTULUI. Difera de la o firma la alta si creste
 *     cand omul mai adauga una. Un catalog scris de mana nu poate avea un rand
 *     pe care nu-l stie nimeni dinainte;
 *   - cifrele n-au istorie pe zile, deci n-ar avea ce desena sub ele;
 *   - dreptul se cere pe ECRAN (`proactivitee.core_applications_dashboard.read`),
 *     o data, nu pe fiecare cifra.
 *
 * S-a cantarit si varianta cealalta: un fel NOU de card in catalog, "cate unul
 * pentru fiecare rand al citirii". Ar fi insemnat umblat in `Catalog`, `Card`,
 * `Board`, `Boards` (asezarea salvata cu mana), `Front`, `CardMarkup` si in
 * paginarea wallboard-ului - noua mii de randuri pe care stau cinci tablouri si
 * peretele din NOC. Pe 10 septembrie 2026 un singur element in plus pe calea
 * aceea a asezat cardurile unul peste altul pe toate cinci deodata. Nu merita,
 * si nu se face de aici.
 *
 * Deci: se ia DESENUL, se lasa MASINARIA. Piesele comune sunt in
 * `dashboard/BoardLook.tsx`, scrise pentru toate ecranele care se muta asa.
 *
 * NU intra in contradictie cu regula lui Bogdan din 6 septembrie 2026 -
 * "tablourile AmCharts clasice nu se porteaza" (`docs/decizie-v4-rescriere.md`,
 * si `DROPPED` din `tools/v4_inventory.py`). Alea sunt `Proactivitee::dashboard`
 * si surorile lui, ecrane care raman in CodeIgniter si nu se mai ating. Asta e
 * `Proactivitee::core_applications_dashboard`, care sta in `MOVED` de saptamani
 * si ruleaza deja pe /app. Nu se converteste nimic; un ecran care e DEJA in v4
 * isi schimba haina.
 *
 * -----------------------------------------------------------------------------
 * CULOAREA DALEI DEVINE TONUL CARDULUI
 * -----------------------------------------------------------------------------
 *
 * API-ul da o culoare pe aplicatie, si aia E starea ei
 * (`DbalCoreAppPagesSource::coreDashboard`): verde cand toate masinile raspund,
 * galben cand macar una e in avertisment, rosu cand macar una e in pericol, si
 * gri cand aplicatia n-are nicio masina.
 *
 * Se citeste culoarea, nu se socoteste din nou din `critical`/`warning`: daca
 * maine regula se schimba in API, ecranul o urmeaza singur, si nu se despart
 * doua socoteli ale aceluiasi lucru - vezi ce a patit taxonomia evenimentelor.
 *
 * Vechiul desen punea culoarea pe TOATA dala, cu text alb peste - de-aia noua
 * din unsprezece culori ale portalului picau pragul de contrast. Desenul nou o
 * pune pe cifra: `.odb-t-crit` face numarul rosu. Acelasi inteles, citit la fel
 * de repede, si de data asta citibil.
 */

type App = { app_id: number; app_name: string; icon: string; devices: number; critical: number; warning: number; color: string }

export function CoreApplicationsDashboardPage() {
  const query = useQuery(coreApplicationsDashboardQuery())
  const counters = (query.data?.counters ?? {}) as Record<string, number>
  const apps = (query.data?.applications ?? []) as App[]
  const appsRange = Number(query.data?.apps_range ?? 0)
  const infraRange = Number(query.data?.infrastructure_range ?? 0)

  return (
    <Content>
      <BoardLook>
        <BoardStrip>
          <InfraStat icon="fa-tasks" value={counters.virtual_infrastructure ?? 0} title="Virtual Infrastructure" />
          <InfraStat icon="fa-server" value={counters.physical_servers ?? 0} title="Servers" />
          <InfraStat icon="fa-hdd" value={counters.data_storage ?? 0} title="Data Storage" />
          <InfraStat icon="fa-sitemap" value={counters.network_infrastructure ?? 0} title="Network Infrastructure" />
          <InfraStat icon="fa-sitemap" value={counters.development_infrastructure ?? 0} title="Development Infrastructure" />
        </BoardStrip>

        <BoardGrid>
          <StatusCard label="Applications Status" icon="fa-layer-group" value={appsRange} />
          <StatusCard label="Infrastructure Status" icon="fa-network-wired" value={infraRange} />
          {apps.map((app) => (
            <ApplicationCard app={app} key={app.app_id} />
          ))}
        </BoardGrid>
      </BoardLook>
    </Content>
  )
}

/**
 * Un contor de infrastructura: aceleasi trei lucruri ca dala veche
 * `dashboard-stat` - cifra (sau "100%" cand nu e nimic cazut), UP / DOWN, si
 * eticheta din josul dalei, care acum e titlul.
 *
 * Regula "cate sunt cazute" e neschimbata: peste zero inseamna DOWN si rosu,
 * zero inseamna UP si verde, si atunci se scrie "100%" in loc de "0", exact ca
 * in `counters.php`.
 */
function InfraStat({ icon, value, title }: { icon: string; value: number; title: string }) {
  const down = value > 0
  return (
    <BoardStat
      module="proactivitee"
      tone={down ? 'crit' : 'ok'}
      icon={icon}
      value={down ? value : 100}
      unit={down ? undefined : '%'}
      kicker={down ? 'Down' : 'Up'}
      title={title}
    />
  )
}

/**
 * Cele doua procente de stare.
 *
 * Pe cec-master le desena amCharts si au fost SCOASE pe 6 septembrie 2026: sub
 * auditul bancar biblioteca iese din portal, iar desenul nu se muta pe alta
 * unealta. Au ramas procentele, scrise ca atare - si asta e tot ce arata si
 * acum, doar ca pe cardul tablourilor.
 *
 * Bara de dedesubt nu spune nimic in plus: e acelasi procent, desenat, ca sa se
 * vada de departe. N-are semne de prag, fiindca praguri nu exista - alea sunt
 * ale cardurilor din catalog.
 *
 * Iconita e podoaba, nu informatie: dala veche n-avea niciuna, dar cardul
 * tablourilor isi poarta placuta modulului, si fara ea cele doua ar fi aratat a
 * jumatate de card.
 */
function StatusCard({ label, icon, value }: { label: string; icon: string; value: number }) {
  const percent = Math.round(value * 100)
  return (
    <BoardCard module="proactivitee" icon={icon} value={percent} unit="%" kicker="Core applications" title={label}>
      <BoardScale percent={percent} />
    </BoardCard>
  )
}

/**
 * O aplicatie. Cele patru lucruri pe care le arata dala veche, toate:
 *
 *   iconita aleasa de client   -> placuta cardului
 *   numarul de masini + disc   -> cifra cardului, cu discul ca unitate
 *   numele aplicatiei          -> titlul cardului
 *   cate merg / cate nu        -> randul de jos, cu aceleasi doua degete
 *
 * Fata iconitei nu se scrie aici: vine din `appIcon.ts`, de unde o cere si
 * alesul de iconite din fila Settings a listei. Acolo sta scris si de ce e
 * `fa` si nu `far`, cu masuratorile. Doua copii ar fi insemnat ca omul alege
 * dintr-o fata si primeste alta - chiar greseala pe care asta o repara.
 */
function ApplicationCard({ app }: { app: App }) {
  const unwell = app.warning + app.critical
  const well = app.devices - unwell

  return (
    <BoardCard
      module="proactivitee"
      tone={toneOfColor(app.color)}
      icon={app.icon || DEFAULT_APP_ICON}
      iconFace={APP_ICON_FACE}
      value={app.devices}
      unit={<i className="far fa-hdd" aria-hidden="true" />}
      kicker="Core application"
      title={app.app_name}
      foot={
        <>
          <span className="odb-of">
            {well} <i className="far fa-thumbs-up" aria-hidden="true" />
          </span>
          <span className="odb-of">
            {unwell} <i className="far fa-thumbs-down" aria-hidden="true" />
          </span>
        </>
      }
    />
  )
}
